import { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useSite } from "@/contexts/SiteContent";
import RichText from "@/components/RichText";

export default function ContactPage() {
  const { content } = useSite();
  const cp = content?.contact_page || {};
  const hero = cp.hero || {};
  const footer = content?.footer || {};
  const contact = footer.contact || {};
  const offices = footer.offices || [];
  const mapEmbed = footer.map_embed_url || "";

  const phone = contact.phone || "";
  const email = contact.email || "";
  const wa = (contact.whatsapp || "").replace(/\D/g, "");

  // If an office is flagged `show_on_map`, auto-derive a Google Maps embed URL
  // from its address (no API key needed). Otherwise fall back to the manually
  // configured embed URL from Admin → Content → Footer.
  const pinnedOffice = offices.find(o => o && o.show_on_map && (o.address || "").trim());
  const mapSrc = pinnedOffice
    ? `https://www.google.com/maps?q=${encodeURIComponent(pinnedOffice.address)}&output=embed`
    : mapEmbed;

  const [form, setForm] = useState({ name:"", email:"", phone:"", subject:"", message:"" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll respond within 24 hours.");
      setForm({ name:"", email:"", phone:"", subject:"", message:"" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send message");
    } finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-20">
      <motion.div initial={{opacity:0, y:14}} animate={{opacity:1, y:0}} transition={{duration:0.5}}>
        <div className="overline">{hero.eyebrow || "Contact"}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 max-w-3xl">{hero.title}</h1>
        <RichText html={hero.subtitle} variant="subtitle" className="mt-6 max-w-3xl"/>
      </motion.div>

      <div className="mt-12 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-4">
          {[
            { test: "contact-phone", href: `tel:${phone.replace(/\s/g,'')}`, icon: Phone, label: "Call us", val: phone },
            { test: "contact-email", href: `mailto:${email}`, icon: Mail, label: "Email", val: email },
            { test: "contact-whatsapp", href: `https://wa.me/${wa}`, icon: MessageCircle, label: "WhatsApp", val: "Chat now", target: "_blank" },
          ].filter(x => x.val).map((it, i) => (
            <motion.a key={it.test} href={it.href} target={it.target} rel="noreferrer" data-testid={it.test}
              initial={{opacity:0, x:-12}} whileInView={{opacity:1, x:0}} viewport={{once:true}} transition={{delay: i*0.06}}
              whileHover={{ scale: 1.01 }}
              className="card-flat p-5 flex items-center gap-4">
              <div className="h-11 w-11 grid place-items-center rounded-xl bg-saffron/10 text-saffron"><it.icon className="h-5 w-5" strokeWidth={1.8}/></div>
              <div><div className="text-xs uppercase tracking-[0.18em] text-ink-muted">{it.label}</div><div className="font-display font-bold text-lg">{it.val}</div></div>
            </motion.a>
          ))}

          {offices.map((o, i) => (
            <motion.div key={i} initial={{opacity:0, x:-12}} whileInView={{opacity:1, x:0}} viewport={{once:true}} transition={{delay: 0.2 + i*0.05}} className="card-flat p-5 flex items-start gap-4">
              <div className="h-11 w-11 grid place-items-center rounded-xl bg-saffron/10 text-saffron"><MapPin className="h-5 w-5" strokeWidth={1.8}/></div>
              <div><div className="text-xs uppercase tracking-[0.18em] text-ink-muted">{o.heading}</div><div className="font-display font-bold text-base mt-1">{o.address}</div></div>
            </motion.div>
          ))}

          {mapSrc && (
            <motion.div initial={{opacity:0, y:14}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{delay:0.3}} className="rounded-2xl overflow-hidden border border-slate-200 aspect-[4/3]" data-testid="contact-map">
              <iframe title="map" src={mapSrc} className="w-full h-full" loading="lazy"></iframe>
            </motion.div>
          )}
        </div>

        <motion.form onSubmit={submit} initial={{opacity:0, y:16}} animate={{opacity:1, y:0}} transition={{duration:0.5, delay:0.2}}
          className="lg:col-span-7 card-flat p-7 sm:p-9 space-y-5" data-testid="contact-form">
          <div className="overline">Send a message</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Tell us what you need.</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">Name</Label><Input data-testid="contact-form-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} required/></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">Email</Label><Input data-testid="contact-form-email" type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} required/></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">Phone</Label><Input data-testid="contact-form-phone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">Subject</Label><Input data-testid="contact-form-subject" value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})}/></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">Message</Label><Textarea data-testid="contact-form-message" rows={5} value={form.message} onChange={e=>setForm({...form, message:e.target.value})} required/></div>
          <Button data-testid="contact-form-submit" type="submit" disabled={sending} className="btn-saffron rounded-full h-12 px-7">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Send className="h-4 w-4 mr-2"/>} Send message
          </Button>
        </motion.form>
      </div>
    </div>
  );
}
